import React, { useState, useEffect } from "react";
import {
  Shield,
  Users,
  Heart,
  BookOpen,
  TrendingUp,
  AlertTriangle,
  GraduationCap,
  School,
  Home,
  Target,
  Award,
  Briefcase,
} from "lucide-react";

const OVCProject = () => {
  const [animateIn, setAnimateIn] = useState(false);

  useEffect(() => {
    setAnimateIn(true);
  }, []);

  const projectStats = [
    { number: "36", label: "Months Duration", icon: Target },
    { number: "5", label: "Program Components", icon: Briefcase },
    { number: "$360k", label: "Total Budget", icon: Award },
    { number: "Rakai", label: "District Focus", icon: Home },
  ];

  const programPillars = [
    {
      icon: GraduationCap,
      title: "Education Access & Retention",
      description:
        "Ensure vulnerable children can enroll, stay in school, and complete their education through targeted material and financial support.",
      highlights: [
        "Registration of orphans, children with disabilities, and single-mother households",
        "Provision of uniforms, scholastic materials, and assistive devices",
        "School attendance tracking and home follow-up",
        "Support with examination and school-related fees",
      ],
      outcome: "Increased enrollment and reduced dropout rates among vulnerable children",
    },
    {
      icon: Shield,
      title: "School-Based Child Protection",
      description:
        "Strengthen child safeguarding systems within schools to detect, report, and respond to abuse, neglect, and exploitation.",
      highlights: [
        "Establishment of School Child Protection Committees",
        "Training teachers on safeguarding and referral pathways",
        "Child-friendly reporting mechanisms",
        "Linkage to district probation, police, and health services",
        "Community dialogues with parents and caregivers",
      ],
      outcome: "Improved detection, reporting, and response to child protection issues",
    },
    {
      icon: Heart,
      title: "Disability-Inclusive Learning",
      description:
        "Create accessible and inclusive learning environments that enable children with disabilities to participate fully in education.",
      highlights: [
        "Disability inclusion assessments in target schools",
        "Provision of basic assistive learning aids",
        "Teacher training on inclusive education practices",
        "Infrastructure support (ramps, accessible latrines)",
      ],
      outcome: "Improved school participation and learning for children with disabilities",
    },
    {
      icon: School,
      title: "Support to Public Schools",
      description:
        "Strengthen under-resourced public schools to improve learning conditions and student performance for all learners.",
      highlights: [
        "Teaching and learning materials (textbooks, desks, science kits)",
        "Support to school feeding initiatives",
        "Minor rehabilitation of critical infrastructure",
        "Teacher-led remedial learning programs",
      ],
      outcome: "Improved learning conditions and student performance in public schools",
    },
    {
      icon: Award,
      title: "Merit-Based Scholarships",
      description:
        "Open pathways for high-achieving learners from low- and middle-income families to access quality private education.",
      highlights: [
        "Transparent selection of high-achieving learners",
        "Partial or full scholarships to accredited private schools",
        "Academic mentoring and psychosocial support",
        "Performance tracking and annual review",
      ],
      outcome: "Improved academic outcomes and long-term opportunities for gifted learners",
    },
  ];

  const targetGroups = [
    "Orphans and vulnerable children (OVC)",
    "Children with disabilities",
    "Children from single-mother-headed households",
    "Under-resourced public primary and secondary schools",
    "High-achieving children from low- and middle-income families",
  ];

  const barriers = [
    "Inability to meet school-related costs (fees, uniforms, materials)",
    "Child labor, neglect, and early marriage",
    "Limited disability-inclusive learning environments",
    "Weak school-based child protection systems",
    "Under-resourced schools with inadequate teaching materials",
  ];

  const crossCuttingThemes = [
    {
      title: "Equity & Inclusion",
      description: "Focus on marginalized children",
      icon: Users,
    },
    {
      title: "Child Rights & Protection",
      description: "Safety and dignity at the center",
      icon: Shield,
    },
    {
      title: "Gender Sensitivity",
      description: "Special attention to girls",
      icon: Heart,
    },
    {
      title: "Community Ownership",
      description: "Engagement of parents, SMCs, and local leaders",
      icon: Home,
    },
  ];

  const budgetBreakdown = [
    { category: "Education Access & Retention Support", amount: 70000, percentage: 19.4 },
    { category: "School-Based Child Protection", amount: 45000, percentage: 12.5 },
    { category: "Disability-Inclusive Education", amount: 40000, percentage: 11.1 },
    { category: "Support to Public Schools", amount: 90000, percentage: 25.0 },
    { category: "Merit-Based Scholarships", amount: 85000, percentage: 23.6 },
    { category: "Monitoring & Coordination", amount: 30000, percentage: 8.3 },
  ];

  return (
    <div className="project-page">
      {/* Bootstrap CSS */}
      <link
        href="https://cdn.jsdelivr.net/npm/bootstrap@5.3.0/dist/css/bootstrap.min.css"
        rel="stylesheet"
      />

      {/* Urgent Banner */}
      <section
        className="urgent-banner"
        role="note"
        aria-label="Education access callout"
      >
        <div className="container">
          <div className="urgent-content">
            <span className="urgent-icon">
              <AlertTriangle size={20} />
            </span>
            <p className="urgent-text">
              <strong>Vulnerable children face compounded barriers to education.</strong>{" "}
              Help us keep them safe, in school, and learning.
            </p>
          </div>
        </div>
      </section>

      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-background"></div>
        <div className="hero-overlay"></div>
        <div className="container position-relative">
          <div className="row align-items-center min-vh-80">
            <div className="col-lg-6">
              <div className="hero-content">
                <div className="hero-badge">SAFE & STAY IN SCHOOL</div>
                <h1 className="hero-title">Orphaned & Vulnerable Children Support Project</h1>
                <p className="hero-subtitle">
                  Ensuring Education Access, Retention, and Child Protection
                </p>
                <p className="hero-description">
                  An integrated, equity-focused intervention that keeps vulnerable children safe, 
                  in school, and learning—while strengthening schools and opening pathways for 
                  high-performing learners to thrive in Rakai District.
                </p>
                <div className="hero-stats-grid">
                  {projectStats.map((stat, index) => (
                    <div key={index} className="stat-card">
                      <stat.icon size={24} className="stat-icon" />
                      <div className="stat-number">{stat.number}</div>
                      <div className="stat-label">{stat.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="col-lg-6">
              <div className="hero-image-container">
                <div className="project-image">
                  <img
                    src="/images/projects/ovc-hero.jpeg"
                    alt="OVC Support Project"
                    className="hero-image"
                    onError={(e) => {
                      e.target.style.display = "none";
                      e.target.nextSibling.style.display = "flex";
                    }}
                  />
                  <div className="image-placeholder">
                    <GraduationCap size={60} />
                    <span>Education & Child Protection</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Problem Statement Section */}
      <section className="problem-section">
        <div className="container">
          <div className="row">
            <div className="col-lg-6">
              <div
                className={`section-header ${animateIn ? "animate-in" : ""}`}
              >
                <div className="section-badge">The Challenge</div>
                <h2 className="section-title">
                  Education Exclusion in Rural Uganda
                </h2>
                <p className="section-description">
                  Despite progress in expanding access to basic education, vulnerable children 
                  in Rakai District face compounded barriers that keep them out of school or 
                  at risk of dropout—while academically gifted children from low-income families 
                  cannot access quality education.
                </p>
              </div>

              <div className="target-groups-card">
                <h4 className="card-subtitle-small">Target Groups</h4>
                <ul className="target-list">
                  {targetGroups.map((group, index) => (
                    <li key={index}>
                      <Users size={16} />
                      <span>{group}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="col-lg-6">
              <div className="barriers-card">
                <h3 className="card-title">
                  Compounded Barriers to Education
                </h3>
                <p className="card-subtitle">
                  Vulnerable children face multiple, intersecting challenges:
                </p>
                <ul className="barriers-list">
                  {barriers.map((barrier, index) => (
                    <li key={index}>
                      <AlertTriangle size={18} />
                      <span>{barrier}</span>
                    </li>
                  ))}
                </ul>
                <div className="call-to-action-box">
                  <p>
                    <strong>
                      There is a need for an integrated, equity-focused education and 
                      child protection intervention.
                    </strong>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Project Goal Section */}
      <section className="goal-section">
        <div className="container">
          <div className="goal-card">
            <div className="row align-items-center">
              <div className="col-lg-8">
                <div className="section-badge mb-3">Project Goal</div>
                <h2 className="goal-title">
                  Safe, In School, and Learning
                </h2>
                <p className="goal-description">
                  To improve access, retention, safety, and learning outcomes for vulnerable 
                  children in Rakai District through school-based child protection, targeted 
                  education support, and equitable access to quality schooling.
                </p>
              </div>
              <div className="col-lg-4">
                <div className="goal-icon-container">
                  <Target size={80} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Program Components / Pillars */}
      <section className="pillars-section">
        <div className="container">
          <div className="row">
            <div className="col-12">
              <div
                className={`section-header text-center ${
                  animateIn ? "animate-in" : ""
                }`}
              >
                <div className="section-badge">Program Components</div>
                <h2 className="section-title">
                  Five Pillars of Comprehensive Support
                </h2>
                <p className="section-description">
                  An integrated approach that addresses education access, child protection, 
                  disability inclusion, school strengthening, and academic excellence.
                </p>
              </div>
            </div>
          </div>

          <div className="row">
            {programPillars.map((pillar, index) => (
              <div key={index} className="col-lg-6 mb-4">
                <div className="pillar-card">
                  <div className="pillar-icon">
                    <pillar.icon size={32} />
                  </div>
                  <div className="pillar-content">
                    <h3 className="pillar-title">{pillar.title}</h3>
                    <p className="pillar-description">{pillar.description}</p>
                    <div className="pillar-section">
                      <h5 className="pillar-section-title">Key Activities</h5>
                      <ul className="pillar-highlights">
                        {pillar.highlights.map((highlight, idx) => (
                          <li key={idx}>
                            <TrendingUp size={16} />
                            {highlight}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div className="expected-outcome">
                      <strong>Expected Result:</strong> {pillar.outcome}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Cross-Cutting Themes */}
      <section className="themes-section">
        <div className="container">
          <div className="row">
            <div className="col-12">
              <div
                className={`section-header text-center ${
                  animateIn ? "animate-in" : ""
                }`}
              >
                <div className="section-badge">Core Values</div>
                <h2 className="section-title">Cross-Cutting Themes</h2>
                <p className="section-description">
                  Guiding principles that inform every aspect of program design and implementation
                </p>
              </div>
            </div>
          </div>

          <div className="row g-4">
            {crossCuttingThemes.map((theme, index) => (
              <div key={index} className="col-md-6 col-lg-3">
                <div className="theme-card">
                  <div className="theme-icon">
                    <theme.icon size={28} />
                  </div>
                  <h4 className="theme-title">{theme.title}</h4>
                  <p className="theme-description">{theme.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Innovation & Value Addition */}
      <section className="innovation-section">
        <div className="container">
          <div className="innovation-card">
            <div className="row">
              <div className="col-12">
                <div className="section-badge mb-3">Innovation & Value Addition</div>
                <h3 className="innovation-title">What Makes This Project Unique</h3>
              </div>
            </div>
            <div className="row">
              <div className="col-md-6">
                <div className="innovation-item">
                  <BookOpen size={24} />
                  <div>
                    <h5>Integrated Approach</h5>
                    <p>Combines education support and child protection in one program</p>
                  </div>
                </div>
              </div>
              <div className="col-md-6">
                <div className="innovation-item">
                  <Award size={24} />
                  <div>
                    <h5>Balanced Impact</h5>
                    <p>System strengthening (public schools) with individual opportunity (scholarships)</p>
                  </div>
                </div>
              </div>
              <div className="col-md-6">
                <div className="innovation-item">
                  <Heart size={24} />
                  <div>
                    <h5>Disability Inclusion</h5>
                    <p>Prioritizes disability inclusion in rural education settings</p>
                  </div>
                </div>
              </div>
              <div className="col-md-6">
                <div className="innovation-item">
                  <Users size={24} />
                  <div>
                    <h5>Community Linkage</h5>
                    <p>Uses school-community connection to sustain impact</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Budget Section */}
      <section className="budget-section">
        <div className="container">
          <div className="budget-card">
            <h3 className="budget-title">Budget Breakdown (USD)</h3>
            <p className="budget-subtitle">
              Duration: 36 months • Total: <strong>$360,000</strong>
            </p>

            <div className="table-responsive">
              <table className="table budget-table align-middle">
                <thead>
                  <tr>
                    <th scope="col">Budget Component</th>
                    <th scope="col" className="text-end">Amount (USD)</th>
                    <th scope="col" className="text-end">% of Total</th>
                  </tr>
                </thead>
                <tbody>
                  {budgetBreakdown.map((item, index) => (
                    <tr key={index}>
                      <td>{item.category}</td>
                      <td className="text-end">${item.amount.toLocaleString()}</td>
                      <td className="text-end">{item.percentage}%</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <th scope="row">Total Project Budget</th>
                    <th className="text-end">$360,000</th>
                    <th className="text-end">100%</th>
                  </tr>
                </tfoot>
              </table>
            </div>

            <div className="budget-bars">
              {budgetBreakdown.map((item, index) => (
                <div key={index} className="bar">
                  <span>{item.category}</span>
                  <div className="bar-track">
                    <div className="bar-fill" style={{ width: `${item.percentage}%` }} />
                  </div>
                  <strong>{item.percentage}%</strong>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Implementation Partner */}
      <section className="partners-section">
        <div className="container">
          <div className="row">
            <div className="col-12">
              <div className="partners-card">
                <h3 className="partners-title">
                  Implementing Organization
                </h3>
                <div className="organization-info">
                  <div className="org-logo">
                    <Home size={48} />
                  </div>
                  <div className="org-details">
                    <h4>Rakai Community Health Initiative (RACOHI)</h4>
                    <p className="org-description">
                      A grassroots nonprofit community-based Ugandan organization committed to 
                      improving health and education outcomes among underserved populations in 
                      Rakai District.
                    </p>
                    <div className="org-experience">
                      <h5>Core Competencies:</h5>
                      <ul>
                        <li>Community-led programming in education and child protection</li>
                        <li>Collaboration with schools and district education offices</li>
                        <li>Advocacy and stakeholder coordination</li>
                        <li>Integrated support for vulnerable populations</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Monitoring & Evaluation */}
      <section className="monitoring-section">
        <div className="container">
          <div className="monitoring-card">
            <div className="row align-items-center">
              <div className="col-lg-3">
                <div className="monitoring-icon">
                  <BookOpen size={60} />
                </div>
              </div>
              <div className="col-lg-9">
                <h3 className="monitoring-title">
                  Monitoring & Evaluation
                </h3>
                <div className="monitoring-items">
                  <div className="monitoring-item">
                    <TrendingUp size={20} />
                    <span>Baseline and endline assessments</span>
                  </div>
                  <div className="monitoring-item">
                    <TrendingUp size={20} />
                    <span>School attendance and retention tracking</span>
                  </div>
                  <div className="monitoring-item">
                    <TrendingUp size={20} />
                    <span>Child protection case monitoring</span>
                  </div>
                  <div className="monitoring-item">
                    <TrendingUp size={20} />
                    <span>Academic performance tracking for scholarship recipients</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-card">
            <div className="cta-content">
              <h2 className="cta-title">
                Support Vulnerable Children
              </h2>
              <p className="cta-description">
                Join us in ensuring that every child in Rakai District has the opportunity 
                to learn, grow, and thrive in a safe and supportive environment. Your support 
                can transform lives and break cycles of poverty and exclusion.
              </p>
              <div className="cta-stats">
                <div className="cta-stat">
                  <span className="stat-number">5</span>
                  <span className="stat-label">Program components working together</span>
                </div>
                <div className="cta-stat">
                  <span className="stat-number">36</span>
                  <span className="stat-label">Months to create lasting change</span>
                </div>
                <div className="cta-stat">
                  <span className="stat-number">1000s</span>
                  <span className="stat-label">Of children to benefit</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Styles */}
      <style jsx>{`
        :root {
          --primary-color: #f59e0b;
          --primary-dark: #d97706;
          --primary-light: #fbbf24;
          --accent-orange: #f59e0b;
          --text-dark: #1f2937;
          --text-gray: #6b7280;
          --text-light: #9ca3af;
          --white: #ffffff;
          --light-bg: #f8fafc;
          --border-light: rgba(0, 0, 0, 0.06);
          --success-green: #10b981;
          --warning-red: #ef4444;
        }

        /* Urgent Banner */
        .urgent-banner {
          background: linear-gradient(135deg, #d97706, #f59e0b);
          color: #fff;
          padding: 12px 0;
        }

        .urgent-content {
          display: flex;
          align-items: center;
          gap: 12px;
          justify-content: center;
          text-align: center;
        }

        .urgent-icon {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          background: rgba(255, 255, 255, 0.2);
          border-radius: 999px;
          padding: 6px;
        }

        .urgent-text {
          margin: 0;
          font-size: 0.98rem;
          line-height: 1.4;
          font-weight: 500;
        }

        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        body {
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto",
            sans-serif;
          line-height: 1.6;
          color: var(--text-dark);
        }

        /* Hero Section */
        .hero-section {
          position: relative;
          min-height: 85vh;
          display: flex;
          align-items: center;
          overflow: hidden;
          padding: 60px 0 40px;
        }
        .hero-background {
          position: absolute;
          inset: 0;
          background: linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%);
          z-index: 0;
        }
        .hero-overlay {
          position: absolute;
          inset: 0;
          background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><circle cx="20" cy="20" r="2" fill="rgba(245,158,11,0.05)"/><circle cx="80" cy="80" r="1.5" fill="rgba(245,158,11,0.03)"/></svg>')
            repeat;
          z-index: 0;
        }

        .hero-content {
          position: relative;
          z-index: 2;
        }
        .hero-badge {
          display: inline-block;
          padding: 8px 20px;
          background: rgba(245, 158, 11, 0.1);
          color: var(--primary-color);
          border-radius: 50px;
          font-size: 14px;
          font-weight: 600;
          margin-bottom: 20px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
        .hero-title {
          font-size: clamp(2.5rem, 5vw, 4.5rem);
          font-weight: 800;
          color: var(--text-dark);
          margin-bottom: 16px;
          line-height: 1.1;
        }
        .hero-subtitle {
          font-size: 1.5rem;
          color: var(--primary-color);
          font-weight: 600;
          margin-bottom: 20px;
          line-height: 1.3;
        }
        .hero-description {
          font-size: 1.15rem;
          color: var(--text-gray);
          margin-bottom: 32px;
          line-height: 1.7;
          max-width: 600px;
        }

        .hero-stats-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 16px;
          margin-top: 24px;
        }

        .stat-card {
          background: white;
          padding: 16px;
          border-radius: 12px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
          text-align: center;
          transition: transform 0.3s ease;
        }

        .stat-card:hover {
          transform: translateY(-4px);
        }

        .stat-icon {
          color: var(--primary-color);
          margin-bottom: 6px;
        }

        .stat-number {
          display: block;
          font-size: 1.5rem;
          font-weight: 700;
          color: var(--text-dark);
          margin-bottom: 4px;
        }

        .stat-label {
          display: block;
          font-size: 0.85rem;
          color: var(--text-gray);
        }

        .hero-image-container {
          position: relative;
          z-index: 2;
        }
        .project-image {
          position: relative;
          width: 100%;
          height: 450px;
          border-radius: 20px;
          overflow: hidden;
          box-shadow: 0 20px 60px rgba(245, 158, 11, 0.25);
        }
        .hero-image {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
        .image-placeholder {
          display: none;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          height: 100%;
          background: linear-gradient(
            135deg,
            var(--primary-color),
            var(--primary-dark)
          );
          color: white;
          text-align: center;
          gap: 16px;
        }

        /* Problem Section */
        .problem-section {
          padding: 60px 0;
          background: white;
        }

        .target-groups-card {
          background: var(--light-bg);
          padding: 24px;
          border-radius: 12px;
          margin-top: 24px;
        }

        .card-subtitle-small {
          font-size: 1.1rem;
          font-weight: 600;
          color: var(--text-dark);
          margin-bottom: 16px;
        }

        .target-list {
          list-style: none;
          padding: 0;
          margin: 0;
        }

        .target-list li {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 10px;
          color: var(--text-gray);
          font-size: 0.95rem;
        }

        .target-list li svg {
          color: var(--primary-color);
          flex-shrink: 0;
        }

        .barriers-card {
          background: white;
          border-radius: 20px;
          padding: 32px;
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
          border: 1px solid var(--border-light);
          height: 100%;
        }

        .card-title {
          font-size: 1.75rem;
          font-weight: 700;
          color: var(--text-dark);
          margin-bottom: 12px;
        }

        .card-subtitle {
          color: var(--text-gray);
          margin-bottom: 20px;
        }

        .barriers-list {
          list-style: none;
          padding: 0;
          margin: 0 0 24px 0;
        }

        .barriers-list li {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          margin-bottom: 12px;
          color: var(--text-gray);
          font-size: 0.95rem;
        }

        .barriers-list li svg {
          color: var(--warning-red);
          flex-shrink: 0;
          margin-top: 2px;
        }

        .call-to-action-box {
          background: linear-gradient(135deg, #d97706, #f59e0b);
          padding: 20px;
          border-radius: 12px;
          color: white;
        }

        .call-to-action-box p {
          margin: 0;
          font-size: 1.05rem;
          line-height: 1.6;
        }

        /* Goal Section */
        .goal-section {
          padding: 60px 0;
          background: var(--light-bg);
        }

        .goal-card {
          background: white;
          border-radius: 20px;
          padding: 40px;
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
          border: 1px solid var(--border-light);
        }

        .goal-title {
          font-size: 2.5rem;
          font-weight: 700;
          color: var(--text-dark);
          margin-bottom: 16px;
          line-height: 1.2;
        }

        .goal-description {
          font-size: 1.2rem;
          color: var(--text-gray);
          line-height: 1.7;
          margin: 0;
        }

        .goal-icon-container {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 140px;
          height: 140px;
          background: linear-gradient(
            135deg,
            var(--primary-color),
            var(--primary-dark)
          );
          border-radius: 50%;
          color: white;
          margin: 0 auto;
        }

        /* Pillars Section */
        .pillars-section {
          padding: 60px 0;
          background: white;
        }
        .section-header {
          margin-bottom: 48px;
          opacity: 0;
          transform: translateY(30px);
          transition: all 0.8s ease;
        }
        .section-header.animate-in {
          opacity: 1;
          transform: translateY(0);
        }
        .section-badge {
          display: inline-block;
          padding: 6px 16px;
          background: rgba(245, 158, 11, 0.1);
          color: var(--primary-color);
          border-radius: 50px;
          font-size: 12px;
          font-weight: 600;
          margin-bottom: 16px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
        .section-title {
          font-size: clamp(2rem, 3vw, 2.5rem);
          font-weight: 700;
          color: var(--text-dark);
          margin-bottom: 16px;
          line-height: 1.2;
        }
        .section-description {
          font-size: 1.1rem;
          color: var(--text-gray);
          margin-bottom: 24px;
          line-height: 1.6;
          max-width: 800px;
          margin-left: auto;
          margin-right: auto;
        }
        .pillar-card {
          background: white;
          border-radius: 20px;
          padding: 32px;
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
          transition: all 0.3s ease;
          height: 100%;
          border: 1px solid var(--border-light);
        }
        .pillar-card:hover {
          transform: translateY(-8px);
          box-shadow: 0 16px 48px rgba(245, 158, 11, 0.15);
        }
        .pillar-icon {
          width: 80px;
          height: 80px;
          background: linear-gradient(
            135deg,
            var(--primary-color),
            var(--primary-dark)
          );
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          margin-bottom: 20px;
        }
        .pillar-title {
          font-size: 1.5rem;
          font-weight: 700;
          color: var(--text-dark);
          margin-bottom: 12px;
        }
        .pillar-description {
          color: var(--text-gray);
          margin-bottom: 20px;
          line-height: 1.6;
        }
        .pillar-section {
          margin-bottom: 16px;
        }
        .pillar-section-title {
          font-size: 1rem;
          font-weight: 600;
          color: var(--text-dark);
          margin-bottom: 10px;
        }
        .pillar-highlights {
          list-style: none;
          padding: 0;
          margin: 0;
        }
        .pillar-highlights li {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          margin-bottom: 8px;
          color: var(--text-gray);
          font-size: 0.95rem;
        }
        .pillar-highlights li svg {
          color: var(--success-green);
          flex-shrink: 0;
          margin-top: 2px;
        }
        .expected-outcome {
          background: rgba(245, 158, 11, 0.08);
          padding: 14px;
          border-radius: 8px;
          font-size: 0.95rem;
          color: var(--text-gray);
          margin-top: 16px;
        }
        .expected-outcome strong {
          color: var(--primary-color);
        }

        /* Themes Section */
        .themes-section {
          padding: 60px 0;
          background: var(--light-bg);
        }

        .theme-card {
          background: white;
          border-radius: 16px;
          padding: 28px;
          box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06);
          transition: all 0.3s ease;
          height: 100%;
          text-align: center;
        }

        .theme-card:hover {
          transform: translateY(-8px);
          box-shadow: 0 8px 24px rgba(245, 158, 11, 0.15);
        }

        .theme-icon {
          width: 60px;
          height: 60px;
          background: linear-gradient(
            135deg,
            var(--primary-color),
            var(--primary-dark)
          );
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          margin: 0 auto 16px;
        }

        .theme-title {
          font-size: 1.2rem;
          font-weight: 700;
          color: var(--text-dark);
          margin-bottom: 10px;
        }

        .theme-description {
          font-size: 0.95rem;
          color: var(--text-gray);
          margin: 0;
          line-height: 1.6;
        }

        /* Innovation Section */
        .innovation-section {
          padding: 60px 0;
          background: white;
        }

        .innovation-card {
          background: var(--light-bg);
          border-radius: 20px;
          padding: 40px;
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
        }

        .innovation-title {
          font-size: 2rem;
          font-weight: 700;
          color: var(--text-dark);
          margin-bottom: 32px;
        }

        .innovation-item {
          display: flex;
          gap: 16px;
          align-items: flex-start;
          background: white;
          padding: 24px;
          border-radius: 12px;
          margin-bottom: 20px;
        }

        .innovation-item svg {
          color: var(--primary-color);
          flex-shrink: 0;
          margin-top: 4px;
        }

        .innovation-item h5 {
          font-size: 1.1rem;
          font-weight: 700;
          color: var(--text-dark);
          margin-bottom: 6px;
        }

        .innovation-item p {
          font-size: 0.95rem;
          color: var(--text-gray);
          margin: 0;
          line-height: 1.6;
        }

        /* Budget Section */
        .budget-section {
          padding: 60px 0;
          background: var(--light-bg);
        }

        .budget-card {
          background: white;
          border-radius: 20px;
          padding: 40px;
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
          border: 1px solid var(--border-light);
        }

        .budget-title {
          font-size: clamp(1.6rem, 2.5vw, 2rem);
          font-weight: 700;
          margin-bottom: 6px;
          color: var(--text-dark);
        }

        .budget-subtitle {
          color: var(--text-gray);
          margin-bottom: 24px;
        }

        .budget-table thead th {
          border-bottom: 2px solid var(--border-light);
          color: var(--text-dark);
        }

        .budget-table tbody td,
        .budget-table tfoot th {
          vertical-align: middle;
        }

        .budget-table tfoot th {
          border-top: 2px solid var(--border-light);
          font-weight: 700;
        }

        .budget-bars {
          display: grid;
          grid-template-columns: 1fr;
          gap: 12px;
          margin-top: 20px;
        }

        .bar {
          display: grid;
          grid-template-columns: 240px 1fr 70px;
          gap: 12px;
          align-items: center;
        }

        .bar span {
          color: var(--text-dark);
          font-weight: 500;
        }

        .bar strong {
          text-align: right;
          color: var(--text-dark);
        }

        .bar-track {
          height: 10px;
          border-radius: 999px;
          background: #eef2f7;
          overflow: hidden;
          border: 1px solid var(--border-light);
        }

        .bar-fill {
          height: 100%;
          width: 0%;
          background: linear-gradient(
            135deg,
            var(--primary-color),
            var(--primary-dark)
          );
          border-radius: 999px;
          transition: width 0.8s ease;
        }

        /* Partners Section */
        .partners-section {
          padding: 60px 0;
          background: white;
        }

        .partners-card {
          background: var(--light-bg);
          border-radius: 20px;
          padding: 48px;
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
        }

        .partners-title {
          font-size: 2rem;
          font-weight: 700;
          color: var(--text-dark);
          margin-bottom: 32px;
          text-align: center;
        }

        .organization-info {
          display: flex;
          gap: 28px;
          align-items: flex-start;
          background: white;
          padding: 32px;
          border-radius: 16px;
        }

        .org-logo {
          width: 100px;
          height: 100px;
          background: linear-gradient(
            135deg,
            var(--primary-color),
            var(--primary-dark)
          );
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          flex-shrink: 0;
        }

        .org-details h4 {
          font-size: 1.5rem;
          font-weight: 700;
          color: var(--text-dark);
          margin-bottom: 12px;
        }

        .org-description {
          color: var(--text-gray);
          margin-bottom: 20px;
          line-height: 1.6;
        }

        .org-experience h5 {
          font-size: 1.1rem;
          font-weight: 600;
          color: var(--text-dark);
          margin-bottom: 10px;
        }

        .org-experience ul {
          list-style: none;
          padding: 0;
          margin: 0;
        }

        .org-experience li {
          padding-left: 24px;
          position: relative;
          margin-bottom: 6px;
          color: var(--text-gray);
        }

        .org-experience li:before {
          content: "•";
          position: absolute;
          left: 8px;
          color: var(--primary-color);
          font-weight: bold;
        }

        /* Monitoring Section */
        .monitoring-section {
          padding: 60px 0;
          background: var(--light-bg);
        }

        .monitoring-card {
          background: white;
          border-radius: 20px;
          padding: 40px;
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
          border: 1px solid var(--border-light);
        }

        .monitoring-icon {
          width: 120px;
          height: 120px;
          background: linear-gradient(
            135deg,
            var(--primary-color),
            var(--primary-dark)
          );
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          margin: 0 auto;
        }

        .monitoring-title {
          font-size: 2rem;
          font-weight: 700;
          color: var(--text-dark);
          margin-bottom: 28px;
        }

        .monitoring-items {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 16px;
        }

        .monitoring-item {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 14px;
          background: var(--light-bg);
          border-radius: 8px;
        }

        .monitoring-item svg {
          color: var(--success-green);
          flex-shrink: 0;
        }

        .monitoring-item span {
          color: var(--text-gray);
          font-size: 0.95rem;
        }

        /* CTA Section */
        .cta-section {
          padding: 60px 0;
          background: white;
        }

        .cta-card {
          background: linear-gradient(135deg, #d97706, #f59e0b);
          border-radius: 24px;
          padding: 60px 48px;
          color: white;
          text-align: center;
        }

        .cta-content {
          max-width: 900px;
          margin: 0 auto;
        }

        .cta-title {
          font-size: 3rem;
          font-weight: 800;
          margin-bottom: 20px;
          line-height: 1.2;
        }

        .cta-description {
          font-size: 1.2rem;
          margin-bottom: 40px;
          opacity: 0.95;
          line-height: 1.7;
        }

        .cta-stats {
          display: flex;
          justify-content: center;
          gap: 40px;
          flex-wrap: wrap;
        }

        .cta-stat {
          display: flex;
          flex-direction: column;
          align-items: center;
        }

        .cta-stat .stat-number {
          font-size: 2.5rem;
          font-weight: 800;
          margin-bottom: 6px;
        }

        .cta-stat .stat-label {
          font-size: 0.95rem;
          opacity: 0.9;
        }

        /* Responsive */
        @media (max-width: 992px) {
          .hero-content {
            margin-bottom: 32px;
          }
          .hero-stats-grid {
            grid-template-columns: repeat(2, 1fr);
          }
          .organization-info {
            flex-direction: column;
            text-align: center;
          }
          .org-logo {
            margin: 0 auto;
          }
          .monitoring-items {
            grid-template-columns: 1fr;
          }
          .bar {
            grid-template-columns: 1fr;
          }
          .bar strong {
            text-align: left;
          }
        }
        @media (max-width: 768px) {
          .hero-section {
            padding: 40px 0 20px;
          }
          .hero-stats-grid {
            grid-template-columns: 1fr;
          }
          .pillar-card {
            padding: 24px 20px;
          }
          .cta-card {
            padding: 48px 24px;
          }
          .cta-title {
            font-size: 2rem;
          }
          .goal-card,
          .partners-card,
          .monitoring-card,
          .budget-card,
          .innovation-card {
            padding: 32px 20px;
          }
          .cta-stats {
            gap: 28px;
          }
          .problem-section,
          .goal-section,
          .pillars-section,
          .themes-section,
          .innovation-section,
          .budget-section,
          .partners-section,
          .monitoring-section,
          .cta-section {
            padding: 40px 0;
          }
        }

        /* Animation */
        .animate-in {
          animation: slideInUp 0.8s ease-out forwards;
        }
        @keyframes slideInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
};

export default OVCProject;